import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { searchByPincode } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PostOffice } from '../types';

const PincodeLookup: React.FC = () => {
  const [pincode, setPincode] = useState('');
  const [selectedPostOffice, setSelectedPostOffice] = useState<PostOffice | null>(null);

  const { data, isLoading, refetch } = useQuery(
    ['pincode', pincode],
    () => searchByPincode(pincode),
    { enabled: false }
  );

  const handleSearch = () => {
    if (pincode.length === 6) {
      refetch();
    }
  };

  const postOffices = data?.[0]?.PostOffice || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Indian PIN Code Directory & Lookup
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-50">
              Get detailed information about any PIN code in India. Search through our comprehensive database of 19,100+ postal codes.
            </p>
          </motion.div>

          {/* Search Section */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <div className="flex flex-col md:flex-row gap-4">
                <input
                  type="text"
                  id="pincode"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                  placeholder="e.g., 110001"
                  maxLength={6}
                />
                <button
                  onClick={handleSearch}
                  disabled={pincode.length !== 6 || isLoading}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {isLoading ? <LoadingSpinner /> : 'Search PIN Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2"
          >
            {postOffices.length > 0 ? (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Post Offices in {pincode}
                </h2>
                <div className="grid gap-6">
                  {postOffices.map((office, index) => (
                    <motion.button
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => setSelectedPostOffice(office)}
                      className={`w-full p-6 rounded-lg border text-left transition-all duration-200 ${
                        selectedPostOffice === office
                          ? 'border-purple-500 bg-purple-50 shadow-md'
                          : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{office.Name}</h3>
                          <p className="text-gray-600 mt-1">
                            {office.BranchType} - {office.District}, {office.State}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          office.DeliveryStatus === "Delivery"
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {office.DeliveryStatus}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {selectedPostOffice && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-8 bg-white rounded-lg shadow-md p-6"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-6">
                      Detailed Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {Object.entries(selectedPostOffice).map(([key, value]) => (
                        <DetailItem key={key} label={key} value={value} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8">
                <div className="text-center">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Enter a PIN Code to Start
                  </h3>
                  <p className="text-gray-600">
                    Search any 6-digit Indian postal code to view detailed information
                  </p>
                </div>

                {/* SEO Content */}
                <div className="mt-12 prose max-w-none">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Understanding Indian PIN Codes
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-600">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">PIN Code Structure</h3>
                      <p className="mb-4">
                        Indian PIN (Postal Index Number) codes are 6-digit numbers that uniquely identify postal
                        delivery areas. The first digit represents the region, the second the sub-region,
                        and the last four digits represent the specific delivery post office.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Coverage</h3>
                      <p>
                        Our database covers all 19,100+ PIN codes across India, including metropolitan
                        cities, rural areas, and special postal zones. Each PIN code entry includes
                        detailed information about post offices, delivery status, and administrative divisions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Quick Tips */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Tips</h2>
              <ul className="space-y-3 text-sm text-gray-600">
                {[
                  'Enter a valid 6-digit PIN code',
                  'First digit (1-9) represents postal region',
                  'Results show all post offices in the area',
                  'Click on a post office for detailed info',
                  'Check delivery status before sending mail',
                ].map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-purple-600 mr-2">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col space-y-1">
    <span className="text-sm text-gray-600">{label}</span>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

export default PincodeLookup;
